import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import config from "@/i18n/config";
import { buildLocalizedPath, detectLanguageFromURL } from "@/i18n/detectLanguage";

/**
 * Client-side fallback for legacy redirects.
 *
 * SEO-critical redirects are implemented as 301s in public/_worker.js. This
 * component only protects local/static SPA runs where the edge worker is absent.
 */
const REDIRECTS: Record<string, string> = {
  "/membership": "/memberships",
  "/application": "/apply",
  "/privileges-points": "/privilege-points",
  "/points-privilege": "/privilege-points",
  "/points-privileges": "/privilege-points",
  "/faq": "/faqs",
  "/download": "/downloads",
  "/download-forms": "/downloads",
  "/about-us": "/about",
  "/how-to-apply": "/member-application-process",

  "/1-year-stay-extension-for-thailand-elite-visa-holders": "/guides/stay-extension",
  "/guides/bank-opening": "/open-bank-account-thailand-elite",
  // /guides/tm30 IS canonical — bare /what-is-tm30-all-about redirects INTO it.
  "/what-is-tm30-all-about": "/guides/tm30",
  "/guides/residence-certificate": "/what-is-a-residence-certificate-and-how-to-get-it",
  "/overstay-explained-the-consequences-of-staying-in-thailand-longer-than-your-visa-allows": "/guides/overstay-rules",
  "/how-to-apply-for-thai-driving-license-with-thailand-elite": "/guides/driving-license",
  "/thailand-elite-perksthai-driving-license-guide": "/guides/driving-license",
  "/90-day-reporting-via-thailand-privilege-cards-sathorn-road-head-office": "/guides/90-day-reporting",
  "/stay-extension-and-elite-personal-liaison-epl-service-for-visa-issues": "/guides/stay-extension",
  "/thailand-privilege-visa-taxes": "/guides/tax-guide",
  "/blog/top-golf-courses-bangkok": "/top-golf-courses-bangkok",
  "/blog/top-10-best-spas-in-bangkok-2025": "/top-10-best-spas-in-bangkok-2025",
  "/tdac-thailand-digital-arrival-card": "/blog/tdac-thailand-digital-arrival-card",
  "/guides/tdac": "/blog/tdac-thailand-digital-arrival-card",
};

const FR_REDIRECTS: Record<string, string> = {
  "/how-to-apply": "/devenir-membre",
  "/member-application-process": "/devenir-membre",
  "/comment-adherer": "/devenir-membre",
  "/comment-postuler": "/devenir-membre",
  "/postuler": "/adherer",
  "/open-bank-account-thailand-elite": "/ouverture-de-compte-bancaire",
  "/guides/bank-opening": "/ouverture-de-compte-bancaire",
  // TM30 on FR domain: /guides/tm30 IS canonical (same slug as EN). Legacy FR
  // slug /declaration-tm30-thailande and /what-is-tm30-all-about both 301 to it.
  "/what-is-tm30-all-about": "/guides/tm30",
  "/declaration-tm30-thailande": "/guides/tm30",
  "/what-is-a-residence-certificate-and-how-to-get-it": "/certificat-de-residence-thailande",
  "/guides/ouverture-de-compte-bancaire": "/ouverture-de-compte-bancaire",
  "/guides/certificat-de-residence": "/certificat-de-residence-thailande",
  "/top-golf-courses-bangkok": "/blog/les-meilleurs-parcours-de-golf-a-bangkok-mise-a-jour-2025",
  "/top-10-best-spas-in-bangkok-2025": "/blog/top-10-des-meilleurs-spas-a-bangkok-en-2025-bien-etre-et-traditions-thailandaises",
  "/tdac-thailand-digital-arrival-card": "/blog/tout-ce-quil-faut-savoir-sur-la-thailand-digital-arrival-card-tdac",
  "/blog/tdac-thailand-digital-arrival-card": "/blog/tout-ce-quil-faut-savoir-sur-la-thailand-digital-arrival-card-tdac",
  "/guides/tdac": "/blog/tout-ce-quil-faut-savoir-sur-la-thailand-digital-arrival-card-tdac",
};

function normalizePath(pathname: string): string {
  const clean = pathname.length > 1 ? pathname.replace(/\/+$/, "") : pathname;
  return clean.toLowerCase() || "/";
}

function domainLanguage(): string | null {
  if (typeof window === "undefined") return null;
  const hostname = window.location.hostname.toLowerCase();
  for (const [lang, domain] of Object.entries(config.domainMap || {})) {
    const domainHost = domain.toLowerCase().replace(/^https?:\/\//, "").replace(/\/$/, "");
    if (hostname === domainHost || hostname === `www.${domainHost}`) return lang;
  }
  return null;
}

function stripLangPrefix(pathname: string, lang: string): string {
  if (pathname === `/${lang}`) return "/";
  if (pathname.startsWith(`/${lang}/`)) return pathname.slice(lang.length + 1) || "/";
  return pathname;
}

function resolveRedirect(pathname: string): string | null {
  const domainLang = domainLanguage();
  const detectedLang = detectLanguageFromURL() || config.defaultLanguage;
  const normalized = normalizePath(pathname);

  const withoutDomainPrefix =
    domainLang ? normalizePath(stripLangPrefix(normalized, domainLang)) : normalized;

  if (domainLang === "fr" && FR_REDIRECTS[withoutDomainPrefix]) {
    return FR_REDIRECTS[withoutDomainPrefix];
  }

  const langMatch = normalized.match(/^\/([a-z]{2})(\/.*)?$/);
  const prefixLang =
    langMatch && config.supportedLanguages.includes(langMatch[1])
      ? langMatch[1]
      : null;
  const barePath = prefixLang ? normalizePath(langMatch[2] || "/") : withoutDomainPrefix;
  if (prefixLang === "fr" && FR_REDIRECTS[barePath]) {
    return buildLocalizedPath(FR_REDIRECTS[barePath], "fr");
  }
  const target = REDIRECTS[barePath];
  if (!target) return null;

  return buildLocalizedPath(target, prefixLang || domainLang || detectedLang);
}

const RedirectHandler = () => {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const target = resolveRedirect(location.pathname);
    if (!target || target === location.pathname) return;
    navigate(`${target}${location.search}${location.hash}`, { replace: true });
  }, [location.hash, location.pathname, location.search, navigate]);

  return null;
};

export default RedirectHandler;
